<script setup>
import { ref } from 'vue'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  name: {
    type: String,
  },
  type: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  id: {
    type: String,
  },
  value: {
    type: String,
  },
  style: {
    type: String,
    default: 'primary',
  },
  error: {
    type: String,
  },
  success: {
    type: String,
  },
  modelValue: {
    type: String,
    default: null,
  },
  hidden: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  path: {
    type: String,
  },
})

const inputStyle = ref(props.style)
const inputClass = ref('')

switch (inputStyle.value) {
  case 'primary':
    inputClass.value =
      'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
    break
  case 'disabled':
    inputClass.value =
      'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 bg-gray-100 disabled shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'
    break
  case 'error':
    inputClass.value =
      'block w-full rounded-md border-0 px-2 py-1.5 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500 sm:text-sm sm:leading-6'
    break
  case 'textarea':
    inputClass.value =
      'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 h-24 max-h-40'
    break
}

defineEmits(['update:modelValue', 'pw-toggle', 'enter-pressed'])
</script>

<template>
  <div>
    <label :for="props.type" class="block text-sm font-medium leading-6 text-gray-900">{{ props.name }}</label>
    <div class="relative mt-2 rounded-md shadow-sm">
      <input
        v-if="type == 'text'"
        :id="props.id"
        :type="props.type"
        :name="props.name"
        :class="inputClass"
        :placeholder="props.placeholder"
        :value="modelValue || value"
        :disabled="disabled"
        aria-invalid="true"
        aria-describedby="error"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <div v-if="type == 'password'">
        <input
          :id="props.id"
          :type="props.type"
          :name="props.name"
          :class="inputClass"
          :placeholder="props.placeholder"
          :value="modelValue || value"
          :disabled="disabled"
          aria-invalid="true"
          aria-describedby="password"
          @input="$emit('update:modelValue', $event.target.value)"
          @keyup.enter="$emit('enter-pressed')"
        />
        <div class="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
          <EyeIcon
            class="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 hover:border-gray-400"
            @click="$emit('pw-toggle')"
          />
        </div>
      </div>
      <div v-if="type == 'password-show'">
        <input
          :id="props.id"
          :type="props.type"
          :name="props.name"
          :class="inputClass"
          :placeholder="props.placeholder"
          :value="modelValue || value"
          :disabled="disabled"
          aria-invalid="true"
          aria-describedby="password"
          @input="$emit('update:modelValue', $event.target.value)"
        />
        <div class="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
          <EyeSlashIcon
            class="inline-flex items-center rounded border border-gray-200 px-1 font-sans text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 hover:border-gray-400"
            @click="$emit('pw-toggle')"
          />
        </div>
      </div>

      <input
        v-if="type == 'email'"
        :id="props.id"
        :type="props.type"
        :name="props.name"
        :class="inputClass"
        :placeholder="props.placeholder"
        :value="modelValue || value"
        :disabled="disabled"
        aria-invalid="true"
        aria-describedby="email"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <textarea
        v-if="type == 'description'"
        :id="props.id"
        maxlength="255"
        :type="props.type"
        :name="props.name"
        :class="inputClass"
        :placeholder="props.placeholder"
        :value="modelValue"
        aria-invalid="true"
        aria-describedby="error"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <div v-if="type == 'domain'">
        <div>
          <div class="flex">
            <input
              :id="props.id"
              type="text"
              :value="modelValue"
              :name="props.name"
              :placeholder="props.placeholder"
              class="block w-full rounded-r-none rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              aria-invalid="true"
              aria-describedby="domain"
              @input="$emit('update:modelValue', $event.target.value)"
            />
            <div class="inset-y-0 left-0 flex items-center rounded-md rounded-l-none border border-l-0 border-gray-300 bg-gray-100 px-2.5 text-gray-700">
              .emailpref.com
            </div>
          </div>
        </div>
      </div>

      <div v-if="type == 'path'">
        <div class="flex">
          <div
            class="max-w-[40%] overflow-scroll inset-y-0 left-0 flex items-center rounded-md rounded-r-none border border-r-0 border-gray-300 bg-gray-100 px-2.5 text-gray-500"
          >
            {{ `https://${props.path}` }}
          </div>
          <input
            :id="props.id"
            type="text"
            :value="modelValue"
            :name="props.name"
            :placeholder="props.placeholder"
            class="block w-full rounded-l-none rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            aria-invalid="true"
            aria-describedby="custom_path"
            @input="$emit('update:modelValue', $event.target.value)"
          />
        </div>
      </div>
      <div v-if="type == 'custom-domain'">
        <div>
          <div class="flex">
            <input
              :id="props.id"
              :type="props.type"
              :name="props.name"
              :class="inputClass"
              :placeholder="props.placeholder"
              :value="modelValue || value"
              :disabled="disabled"
              aria-invalid="true"
              aria-describedby="error"
              @input="$emit('update:modelValue', $event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
    <p v-if="error" id="error" class="mt-1 text-xs text-red-600">{{ $t(props.error) }}</p>
    <p v-if="success" id="error" class="mt-1 text-xs text-green-600">{{ $t(props.success) }}</p>
  </div>
</template>
